/**
 * Analytics provider — initializes PostHog with LGPD consent.
 *
 * Wrap your app once near the root, inside <ClerkProvider> if used.
 *
 * @example
 * ```tsx
 * <AnalyticsProvider
 *   product="gamin_index"
 *   apiKey={env.VITE_POSTHOG_KEY}
 *   apiHost={env.VITE_POSTHOG_HOST}
 *   environment={import.meta.env.MODE as "production" | "development"}
 * >
 *   <App />
 * </AnalyticsProvider>
 * ```
 */
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import {
  initAnalytics,
  grantConsent as grantConsentFn,
  revokeConsent as revokeConsentFn,
  isTracking as isTrackingFn,
} from "../tracker";
import type { AnalyticsConfig } from "../types";

interface AnalyticsContextValue {
  /** True once PostHog has been initialized on the client. */
  initialized: boolean;
  /** True when the user has granted LGPD consent. */
  consented: boolean;
  /** Grant LGPD consent — call from your consent banner "Accept" button. */
  grantConsent: () => void;
  /** Revoke LGPD consent — call from settings "Opt out". */
  revokeConsent: () => void;
}

const AnalyticsContext = createContext<AnalyticsContextValue | null>(null);

const CONSENT_STORAGE_KEY = "engagexp:analytics:consent";

type AnalyticsProviderProps = Omit<AnalyticsConfig, "disabled"> & {
  children: ReactNode;
  /**
   * If true, skip init entirely. Useful for local dev where you don't want
   * PostHog events muddying prod data even with a dev project key.
   */
  disabled?: boolean;
};

export function AnalyticsProvider({
  children,
  disabled,
  ...config
}: AnalyticsProviderProps) {
  const [initialized, setInitialized] = useState(false);
  const [consented, setConsented] = useState(false);

  useEffect(() => {
    if (disabled) return;
    if (!config.apiKey) {
      // eslint-disable-next-line no-console
      console.warn(
        "[@engagexp/analytics] No apiKey provided — skipping init",
      );
      return;
    }

    initAnalytics({ ...config, disabled });
    setInitialized(true);

    // Restore consent from localStorage on mount.
    try {
      const stored = window.localStorage.getItem(CONSENT_STORAGE_KEY);
      if (stored === "granted") {
        grantConsentFn();
        setConsented(true);
      }
    } catch {
      /* SSR / disabled storage — ignore */
    }
    // Intentionally only run once on mount — config is stable per app.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo<AnalyticsContextValue>(
    () => ({
      initialized,
      consented,
      grantConsent: () => {
        grantConsentFn();
        setConsented(true);
        try {
          window.localStorage.setItem(CONSENT_STORAGE_KEY, "granted");
        } catch {
          /* ignore */
        }
      },
      revokeConsent: () => {
        revokeConsentFn();
        setConsented(false);
        try {
          window.localStorage.setItem(CONSENT_STORAGE_KEY, "revoked");
        } catch {
          /* ignore */
        }
      },
    }),
    [initialized, consented],
  );

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
}

/**
 * Access the analytics context (initialized state + consent controls).
 * Throws if used outside <AnalyticsProvider>.
 */
export function useAnalytics(): AnalyticsContextValue {
  const ctx = useContext(AnalyticsContext);
  if (!ctx) {
    throw new Error(
      "useAnalytics must be used inside <AnalyticsProvider>",
    );
  }
  return ctx;
}

/** Convenience hook — true when PostHog is live AND consent has been granted. */
export function useIsTracking(): boolean {
  const { initialized, consented } = useAnalytics();
  return initialized && consented && isTrackingFn();
}
